import * as Sentry from "@sentry/react";

/**
 * Capture an exception with context
 */
export const captureException = (error, context = {}) => {
  Sentry.withScope((scope) => {
    Object.entries(context).forEach(([key, value]) => {
      scope.setContext(key, value);
    });

    Sentry.captureException(error);
  });
};

/**
 * Capture a message with severity level
 */
export const captureMessage = (message, level = "info", context = {}) => {
  Sentry.withScope((scope) => {
    Object.entries(context).forEach(([key, value]) => {
      scope.setContext(key, value);
    });

    Sentry.captureMessage(message, {
      level,
    });
  });
};

/**
 * Set user context
 */
export const setUserContext = (
  userId,
  email = null,
  username = null
) => {
  Sentry.setUser({
    id: userId,
    email,
    username,
  });
};

/**
 * Clear user context
 */
export const clearUserContext = () => {
  Sentry.setUser(null);
};

/**
 * Add breadcrumb
 */
export const addBreadcrumb = (
  message,
  category = "user-action",
  level = "info",
  data = {}
) => {
  Sentry.addBreadcrumb({
    category,
    message,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
};

/**
 * Wrap async functions to automatically capture errors
 */
export const withErrorCapture = async (
  asyncFn,
  errorContext = {}
) => {
  try {
    return await asyncFn();
  } catch (error) {
    captureException(error, errorContext);
    throw error;
  }
};

/**
 * Start a manual span (replacement for deprecated startTransaction)
 */
export const startTransaction = (
  name,
  operation = "custom"
) => {
  return Sentry.startInactiveSpan({
    name,
    op: operation,
  });
};

/**
 * Finish a manual span
 */
export const finishTransaction = (span) => {
  span?.end();
};

/**
 * Log API errors
 */
export const logApiError = (
  error,
  endpoint,
  method = "GET",
  data = {}
) => {
  const context = {
    api: {
      endpoint,
      method,
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      requestData: data,
      responseData: error?.response?.data,
    },
  };

  captureException(error, context);
};

/**
 * Log form errors
 */
export const logFormError = (
  error,
  formName,
  formData = {}
) => {
  captureException(error, {
    form: {
      name: formName,
      data: formData,
    },
  });
};

/**
 * Log validation errors
 */
export const logValidationError = (
  error,
  field,
  value
) => {
  captureMessage(
    `Validation error on ${field}`,
    "error",
    {
      validation: {
        field,
        value,
        error: error?.message,
      },
    }
  );
};

export default {
  captureException,
  captureMessage,
  setUserContext,
  clearUserContext,
  addBreadcrumb,
  withErrorCapture,
  startTransaction,
  finishTransaction,
  logApiError,
  logFormError,
  logValidationError,
};